import { useCallback, useState } from 'react'
import {
  AgentRequest,
  CodeContext,
  Command,
  CommandType,
  ContextUpload,
  FileUpload,
  IntegrationPlatform,
  Metadata,
  MigrationDocumentInput,
  integrationPlatformToJSON
} from '~/protos/agents'
import { ProjectSelectionOption } from '~/store/taskFormSlice'
import { RepositoryBranch } from '~/types/Conversations'
import { parseRepositoryName } from '~/utils/repositoryUtils'
import { BetterToast } from '~/utils/toast'
import { TaskParams } from '../task-forms/BaseTaskForm'
import { SpringIntegrationRefactorRepoSelectionScreen } from './SpringIntegrationRefactorRepoSelectionScreen'

interface SpringIntegrationRefactorContainerProps {
  onClose: () => void
  onTaskSubmit: (taskParams: TaskParams) => Promise<boolean>
}

// Shape of the payload emitted by SpringIntegrationRefactorRepoSelectionScreen.onSave.
// Keep in sync with the corresponding interface declared on that component.
type RepoSavePayload = {
  legacyRepos: RepositoryBranch[]
  legacyOption: ProjectSelectionOption
  legacyS3Paths: string[]
  mulesoftRepos: RepositoryBranch[]
  mulesoftOption?: ProjectSelectionOption
  mulesoftS3Paths: string[]
  platform: IntegrationPlatform
  metadataS3Paths?: string[]
}

export function SpringIntegrationRefactorContainer({
  onClose,
  onTaskSubmit
}: SpringIntegrationRefactorContainerProps) {
  // NOTE: the repo-selection child fetches its own command-scoped repo
  // list via useRepositoryBranches(SPRING_INTEGRATION_REFACTORING); we
  // do not need to pass `useCodeContext().repositories` through.

  // Locks the repo screen into a loading-spinner state while the task is being
  // created. On success the parent navigates to the conversation page (which
  // renders the real endpoint-identification view once the task_id exists); on
  // failure we release it so the user can retry (selections are preserved by
  // the child).
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleRepoSave = useCallback(
    async (payload: RepoSavePayload) => {
      // Build multi_repo_code_context for ALL legacy repositories.
      //
      // ``legacyOption`` is a single-mode tag emitted by
      // ``SpringIntegrationRefactorRepoSelectionScreen`` (``'codeContext'``
      // when ANY git repo is selected, else ``'upload'`` when only
      // uploads exist, else ``'none'``). Earlier this branch was
      // ``if codeContext / else if upload``, which meant a user picking
      // both git repos AND an upload silently lost the upload. Walk both
      // lists unconditionally — they're independent — and rely on the
      // ``canSave`` guard upstream + the empty-context guard below for
      // the truly-nothing case.
      const multiRepoCodeContext: CodeContext[] = []
      for (const repo of payload.legacyRepos ?? []) {
        const [orgName, repoName] = parseRepositoryName(repo.repository)
        multiRepoCodeContext.push(
          CodeContext.create({
            connType: repo.conn_type,
            orgName,
            repoName,
            branchName: repo.branch
          })
        )
      }
      for (const s3Path of payload.legacyS3Paths ?? []) {
        multiRepoCodeContext.push(
          CodeContext.create({
            connType: 'S3',
            s3TarFilePath: s3Path
          })
        )
      }

      // Build auxiliary_code_context for ALL MuleSoft/source repositories
      // (optional). Same fix as above — combine git + uploads instead of
      // letting one silently win.
      const auxiliaryCodeContext: CodeContext[] = []
      for (const repo of payload.mulesoftRepos ?? []) {
        const [orgName, repoName] = parseRepositoryName(repo.repository)
        auxiliaryCodeContext.push(
          CodeContext.create({
            connType: repo.conn_type,
            orgName,
            repoName,
            branchName: repo.branch
          })
        )
      }
      for (const s3Path of payload.mulesoftS3Paths ?? []) {
        auxiliaryCodeContext.push(
          CodeContext.create({
            connType: 'S3',
            s3TarFilePath: s3Path
          })
        )
      }

      // Defence-in-depth against an `onSave` payload that slipped past the
      // `canSave` guard in SpringIntegrationRefactorRepoSelectionScreen
      // (e.g. `legacyOption === 'none'`, or both `legacyRepos` and
      // `legacyS3Paths` empty). Without this, the AgentRequest would ship
      // with zero code context and the backend would start a refactor task
      // with nothing to refactor.
      if (multiRepoCodeContext.length === 0) {
        BetterToast.error('At least one source repository is required')
        return
      }

      // Defence-in-depth on the platform field for the same reason — the
      // backend forwards ``payload.platform`` straight into
      // ``MigrationDocumentInput.platform`` with no validation. Reject
      // BOTH ``UNRECOGNIZED`` (-1, protobuf-ts out-of-schema sentinel)
      // AND ``UNKNOW_INTEGRATION_PLATFORM`` (0, the proto-wire default
      // a stored payload can deserialise to). Mirrors the same pair check
      // in ``SpringIntegrationRefactorRepoSelectionScreen.isPlatformSelected``.
      if (
        payload.platform === IntegrationPlatform.UNRECOGNIZED ||
        payload.platform === IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM
      ) {
        BetterToast.error('Please select a source integration platform')
        return
      }

      // Build metadata with code contexts
      // If single legacy repo, also set codeContext for backward compatibility
      const codeContext = multiRepoCodeContext.length === 1 ? multiRepoCodeContext[0] : undefined

      const metadata = Metadata.create({
        codeContext,
        multiRepoCodeContext,
        auxiliaryCodeContext: auxiliaryCodeContext.length > 0 ? auxiliaryCodeContext : undefined
      })

      // Build MigrationDocumentInput
      const migrationDocInput = MigrationDocumentInput.create({
        platform: payload.platform
      })

      // Build ContextUpload from metadata files (if provided)
      const contextUpload =
        payload.metadataS3Paths && payload.metadataS3Paths.length > 0
          ? ContextUpload.create({
              files: payload.metadataS3Paths.map(s3Path => {
                const fileName = s3Path.split('/').pop() || 'file'

                return FileUpload.create({
                  fileName,
                  fileS3Path: s3Path
                })
              })
            })
          : undefined

      // Build Command
      const command = Command.create({
        commandType: CommandType.SPRING_INTEGRATION_REFACTORING,
        commandString: `Platform: ${integrationPlatformToJSON(payload.platform)}`,
        contextUpload
      })

      // Build AgentRequest with nested command
      const agentRequest = AgentRequest.create({
        command,
        metadata,
        migrationDocInput
      })

      // Submit task using parent's onTaskSubmit handler
      setIsSubmitting(true)
      try {
        const isSuccess = await onTaskSubmit({ request: agentRequest })

        if (!isSuccess) {
          console.error('[SpringIntegrationRefactorContainer] Task submission failed')
          BetterToast.error('Failed to start refactoring task')
          setIsSubmitting(false)
        }
        // On success, leave the screen locked: the parent navigates to the
        // conversation page, so re-enabling would only flicker.
      } catch (error) {
        console.error(
          '[SpringIntegrationRefactorContainer] Failed to submit Spring Integration Refactor task:',
          error
        )
        BetterToast.error('Failed to start refactoring task')
        setIsSubmitting(false)
      }
    },
    [onTaskSubmit]
  )

  // Render repository selection screen
  return (
    <SpringIntegrationRefactorRepoSelectionScreen
      onClose={onClose}
      onSave={handleRepoSave}
      isSubmitting={isSubmitting}
    />
  )
}
